import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import {
  Shield, LogOut, CheckCircle, XCircle, FileText,
  ExternalLink, Search, ShieldCheck, ShieldAlert, Users
} from "lucide-react";
import { toast } from "sonner";

export default function AdminPortal() {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const [ngos, setNgos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [verifyingId, setVerifyingId] = useState(null);

  useEffect(() => {
    loadNgos();
  }, []);

  const loadNgos = async () => {
    try {
      const { data } = await api.get("/admin/ngos");
      setNgos(data);
    } catch (e) {
      toast.error("Failed to load NGOs");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (ngoId) => {
    setVerifyingId(ngoId);
    try {
      await api.post(`/admin/ngos/${ngoId}/verify`);
      setNgos((prev) =>
        prev.map((n) => (n.id === ngoId ? { ...n, is_manually_verified: true } : n))
      );
      toast.success("NGO verified successfully");
    } catch (e) {
      toast.error("Failed to verify NGO");
    } finally {
      setVerifyingId(null);
    }
  };

  const handleUnverify = async (ngoId) => {
    setVerifyingId(ngoId);
    try {
      await api.post(`/admin/ngos/${ngoId}/unverify`);
      setNgos((prev) =>
        prev.map((n) => (n.id === ngoId ? { ...n, is_manually_verified: false } : n))
      );
      toast.success("NGO verification revoked");
    } catch (e) {
      toast.error("Failed to update NGO");
    } finally {
      setVerifyingId(null);
    }
  };

  const handleDownloadDoc = async (userId, docId, filename) => {
    try {
      const res = await api.get(`/admin/ngo-documents/${userId}/${docId}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error("Failed to load document");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const filteredNgos = searchQuery
    ? ngos.filter(
        (n) =>
          n.org_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (n.registration_number || "").toLowerCase().includes(searchQuery.toLowerCase())
      )
    : ngos;

  const verifiedCount = ngos.filter((n) => n.is_manually_verified).length;
  const pendingCount = ngos.filter((n) => !n.is_manually_verified).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-body" data-testid="admin-portal">
      {/* Top Nav */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50" data-testid="admin-nav">
        <div className="max-w-7xl mx-auto h-14 flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <img src="/logo-icon.png" alt="X" className="w-8 h-8 object-contain" />
            <span className="font-bold text-white text-base tracking-tight">
              Impact<span className="text-ix-red-500">X</span> Admin
            </span>
            <Badge className="bg-slate-700 text-slate-300 border-0 text-[10px]">Control Center</Badge>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-400">{user?.email}</span>
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-sans font-bold text-slate-900" data-testid="admin-total-count">{ngos.length}</p>
                <p className="text-xs text-slate-500">Total NGOs</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-2xl font-sans font-bold text-emerald-900" data-testid="admin-verified-count">{verifiedCount}</p>
                <p className="text-xs text-slate-500">Verified</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-sans font-bold text-amber-700" data-testid="admin-pending-count">{pendingCount}</p>
                <p className="text-xs text-slate-500">Pending Review</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* NGO Oversight Table */}
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="admin-ngo-table-card">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="font-sans text-lg text-slate-900">NGO Oversight</CardTitle>
                <CardDescription className="text-slate-500">
                  Review, verify, and manage registered NGOs
                </CardDescription>
              </div>
              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="Search by name or reg number..."
                  className="pl-10 focus-visible:ring-emerald-600"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="admin-search-input"
                />
              </div>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="p-0">
            {filteredNgos.length === 0 ? (
              <div className="p-12 text-center text-slate-500 text-sm">
                {searchQuery ? "No NGOs match your search." : "No NGOs registered yet."}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredNgos.map((ngo) => (
                  <div
                    key={ngo.id}
                    className="px-6 py-4 flex flex-col lg:flex-row lg:items-center gap-4"
                    data-testid={`admin-ngo-row-${ngo.id}`}
                  >
                    {/* NGO Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-slate-900">{ngo.org_name}</h4>
                        {ngo.is_manually_verified && (
                          <Badge className="bg-emerald-600 text-white border-0 text-[10px] px-1.5 py-0 h-5 gap-1" data-testid={`admin-verified-badge-${ngo.id}`}>
                            <CheckCircle className="w-3 h-3" />
                            Verified
                          </Badge>
                        )}
                        {!ngo.is_manually_verified && (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0 h-5">
                            Pending
                          </Badge>
                        )}
                      </div>
                      {ngo.registration_number && (
                        <p className="text-xs text-slate-400 mt-0.5">Reg: {ngo.registration_number}</p>
                      )}
                      {ngo.focus_sectors.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {ngo.focus_sectors.map((s) => (
                            <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-500 border border-slate-200">
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Compliance Status */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="flex items-center gap-1.5 text-xs">
                        {ngo.status_80g ? (
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-slate-300" />
                        )}
                        <span className={ngo.status_80g ? "text-emerald-700 font-medium" : "text-slate-400"}>80G</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs">
                        {ngo.status_12a ? (
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-slate-300" />
                        )}
                        <span className={ngo.status_12a ? "text-emerald-700 font-medium" : "text-slate-400"}>12A</span>
                      </div>
                      <div className="px-2 py-1 bg-slate-50 rounded text-xs font-mono font-bold text-slate-700" data-testid={`admin-xscore-${ngo.id}`}>
                        {ngo.x_score}
                      </div>
                    </div>

                    {/* Documents */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {ngo.documents && ngo.documents.length > 0 ? (
                        ngo.documents.map((doc) => (
                          <button
                            key={doc.id}
                            onClick={() => handleDownloadDoc(ngo.user_id, doc.id, doc.filename)}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 text-xs text-slate-600 hover:border-emerald-300 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                            title={doc.filename}
                            data-testid={`admin-doc-${doc.doc_type}-${ngo.id}`}
                          >
                            <FileText className="w-3.5 h-3.5" />
                            {doc.doc_type.toUpperCase()}
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        ))
                      ) : (
                        <span className="text-xs text-slate-400">No docs</span>
                      )}
                    </div>

                    {/* Verify Action */}
                    <div className="flex-shrink-0">
                      {ngo.is_manually_verified ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600 hover:bg-red-50 text-xs h-8"
                          onClick={() => handleUnverify(ngo.id)}
                          disabled={verifyingId === ngo.id}
                          data-testid={`admin-unverify-btn-${ngo.id}`}
                        >
                          Revoke
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          className="bg-ix-red-600 text-white hover:bg-ix-red-700 shadow-sm text-xs h-8"
                          onClick={() => handleVerify(ngo.id)}
                          disabled={verifyingId === ngo.id}
                          data-testid={`admin-verify-btn-${ngo.id}`}
                        >
                          {verifyingId === ngo.id ? "Verifying..." : "Review & Verify"}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
